// De vier standen van het aanvraagvenster.
//
// De V1-dummy probeerde de hele levenscyclus in één datum te vangen ("nog N
// dagen" / "Verlopen"). De echte data kent vier situaties, en het verschil
// tussen "opent pas op 7 april" en "sluit over drie weken" is precies wat een
// accountmanager wil zien.
//
// ÉÉN REGEL, TWEE LEZERS: de tabelrij en het detailpaneel rekenen hier, zodat
// een regeling nooit twee verschillende badges kan dragen.
//
// De vijfde stand is de eerlijke: 72 van de 119 officiële pagina's noemen géén
// aanvraagperiode. Die krijgen geen groen "doorlopend" — dat zou een belofte
// zijn die de bron niet doet — maar een neutrale badge die zegt dat de pagina
// erover zwijgt.
//
// DE SERVERSTATUS WINT (funding werkend W5, ADR 2026-10-06-funding-werkend.md).
// Sinds W0 rekent de server de status op de dag van lezen (sof.funding.status:
// verlopen venster → gesloten, start in de toekomst → aangekondigd, een gelezen
// 'gesloten' binnen het venster blijft gesloten). Geeft de aanroeper `status`
// mee, dan beslist DIE over de stand (open/dicht, kleur, dimmen); deze module
// doet dan alleen nog de dagtelling ("sluit over 12 dagen", "opent 7 apr") en
// de opmaak. Zonder `status` blijft de oude kalenderstand bestaan (alleen nog
// voor de tests van de dagtelling) — de schermen geven hem altijd mee, zodat
// badge en dimmen uit één bron komen.

import { color } from "../tokens";

export type VensterStand = {
  soort:
    | "doorlopend"
    | "open"
    | "opent"
    | "sluit"
    | "verlopen"
    | "onbekend"
    | "gesloten"
    | "aangekondigd";
  tekst: string;
  kleur: string;
  achtergrond: string;
  /** Aantal dagen tot de eerstvolgende gebeurtenis; null als er geen datum is. */
  dagen: number | null;
  uitleg: string;
};

export type Venster = {
  soort: string;
  start?: string | null;
  eind?: string | null;
};

const DAG = 86_400_000;

/** Dagen tussen vandaag en een ISO-datum; negatief = in het verleden. */
export function dagenTot(iso: string | null | undefined, vandaag: Date): number | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const nul = new Date(vandaag.getFullYear(), vandaag.getMonth(), vandaag.getDate());
  return Math.round((d.getTime() - nul.getTime()) / DAG);
}

export function fmtDatum(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}

/** De status zoals de server hem op de dag van lezen rekent (contract funding.status). */
export type ServerStatus = "open" | "aangekondigd" | "gesloten" | "onbekend";

/**
 * De stand als de server de status al gerekend heeft: de status beslist, het
 * venster levert alleen de dagtelling en de datum in de tekst.
 */
function standVanServer(
  venster: Venster,
  status: ServerStatus,
  vandaag: Date,
  binnenkort: number,
  uitExport: boolean,
  statusHerkomst?: string,
): VensterStand {
  const totStart = dagenTot(venster.start, vandaag);
  const totEind = dagenTot(venster.eind, vandaag);
  const voorbij = totEind !== null && totEind < 0;
  // Herkomst van het venster zelf, zodat een uitleg nooit 'de pagina' noemt
  // als bron van een datum die uit de export komt (reviewronde W5).
  const vensterBron = uitExport
    ? " Dat venster komt uit de redactie-export van vindsubsidies.nl (aug 2026), niet van de officiële pagina."
    : "";

  if (status === "gesloten") {
    return {
      soort: "gesloten",
      tekst: voorbij ? `gesloten sinds ${fmtDatum(venster.eind)}` : "gesloten",
      kleur: color.soft,
      achtergrond: color.surfaceHover,
      dagen: totEind,
      // Na de einddatum heeft niemand per se gekeken: geen bewering over wat
      // de pagina nu wel of niet zegt. Of er gecontroleerd is, zegt de
      // statustekst van de server ('Status vandaag').
      uitleg: voorbij
        ? `Het aanvraagvenster liep tot en met ${fmtDatum(venster.eind)}; een nieuw venster is ons niet bekend.${vensterBron}`
        : "Volgens de officiële pagina of de export kan er nu niet worden aangevraagd.",
    };
  }

  // Een opgeslagen 'open' of 'aangekondigd' met een einddatum die voorbij is:
  // vóór W1 levert de leeslaag nog de bevroren kolom. De regel van de server
  // (sof.funding.status: verlopen eind → gesloten) zou deze stand nooit geven,
  // dus hier geen groene of amber belofte: de kalenderstand 'verlopen', met de
  // reden erbij (reviewronde W5).
  if ((status === "open" || status === "aangekondigd") && voorbij && venster.soort !== "doorlopend") {
    return {
      soort: "verlopen",
      tekst: `gesloten sinds ${fmtDatum(venster.eind)}`,
      kleur: color.soft,
      achtergrond: color.surfaceHover,
      dagen: totEind,
      uitleg:
        `Het aanvraagvenster liep tot en met ${fmtDatum(venster.eind)}. De opgeslagen status ` +
        `('${status}') is nog niet op vandaag herberekend; controleer het bij de verstrekker.${vensterBron}`,
    };
  }

  if (status === "aangekondigd") {
    const later = totStart !== null && totStart > 0;
    return {
      soort: later ? "opent" : "aangekondigd",
      tekst: later ? `opent ${fmtDatum(venster.start)}` : "aangekondigd",
      kleur: color.accentAmber,
      achtergrond: color.accentAmberBg,
      dagen: later ? totStart : null,
      uitleg: later
        ? totStart! <= binnenkort
          ? `Nog niet open: aanvragen kan over ${totStart} dagen. Dit is het moment om een werkgever te bellen.`
          : "Nog niet open; de openingsdatum staat vast."
        : venster.start
          ? `Er is een ronde aangekondigd met start op ${fmtDatum(venster.start)}; dat de aanvraag inmiddels open staat, is niet bevestigd.`
          : "Er is een ronde aangekondigd; een openingsdatum staat nog niet op de pagina.",
    };
  }

  if (status === "open") {
    if (venster.soort === "doorlopend") {
      return {
        soort: "doorlopend",
        tekst: "doorlopend",
        kleur: color.accentGreen,
        achtergrond: "#f0fdf4",
        dagen: null,
        uitleg: "Je kunt het hele jaar door aanvragen; de pagina noemt geen sluitingsdatum.",
      };
    }
    // 'open' zonder enige periode: geen groene belofte die de bron niet doet
    // (zie de kop). De status staat, de periode niet — en waar de status
    // vandaan komt, zegt de badge zelf (reviewronde W5).
    if (venster.soort === "onbekend" || (totStart === null && totEind === null)) {
      const uitDeExport = statusHerkomst === "export";
      return {
        soort: "open",
        tekst: uitDeExport ? "open · uit de export" : "open · geen periode vermeld",
        kleur: color.accentAmber,
        achtergrond: color.accentAmberBg,
        dagen: null,
        uitleg: uitDeExport
          ? "De status 'open' komt uit de redactie-export van vindsubsidies.nl (25-08-2026); " +
            "de officiële pagina noemt geen aanvraagperiode. Controleer het bij de verstrekker."
          : "De status is open, maar er is geen aanvraagperiode vermeld. Controleer het bij de verstrekker.",
      };
    }
    if (totEind !== null && totEind >= 0 && totEind <= binnenkort) {
      return {
        soort: "sluit",
        tekst: totEind === 0 ? "sluit vandaag" : `sluit over ${totEind} dagen`,
        kleur: color.accentRed,
        achtergrond: "#fef2f2",
        dagen: totEind,
        uitleg: `Het venster sluit op ${fmtDatum(venster.eind)}. Daarna kan er niet meer worden aangevraagd.`,
      };
    }
    return {
      soort: "open",
      tekst: totEind !== null && totEind >= 0 ? `open t/m ${fmtDatum(venster.eind)}` : "open",
      kleur: color.accentGreen,
      achtergrond: "#f0fdf4",
      dagen: totEind,
      uitleg:
        totEind !== null && totEind >= 0
          ? `Aanvragen kan tot en met ${fmtDatum(venster.eind)}.`
          : "Volgens de officiële pagina of de export staat de regeling open; er is geen einddatum vermeld.",
    };
  }

  // status onbekend: geen stand verzinnen. Alleen 'doorlopend' is een
  // venster dat zelf iets zegt; een datum zonder status rekenen we hier niet om.
  if (venster.soort === "doorlopend") {
    return {
      soort: "doorlopend",
      tekst: "doorlopend",
      kleur: color.accentGreen,
      achtergrond: "#f0fdf4",
      dagen: null,
      uitleg: "Je kunt het hele jaar door aanvragen; de pagina noemt geen sluitingsdatum.",
    };
  }
  const zonderPeriode = venster.soort === "onbekend" || (totStart === null && totEind === null);
  return {
    soort: "onbekend",
    tekst: zonderPeriode ? "geen periode vermeld" : "status onbekend",
    kleur: color.soft,
    achtergrond: color.surfaceHover,
    dagen: null,
    uitleg: zonderPeriode
      ? "De officiële pagina noemt geen aanvraagperiode. Dat is wat wij daar " +
        "aantroffen, geen aanname dat de regeling doorlopend open staat — " +
        "controleer het bij de verstrekker."
      : "De status is niet bekend; controleer het bij de verstrekker.",
  };
}

export function vensterStand(
  venster: Venster,
  {
    vandaag = new Date(),
    binnenkort = 60,
    uitExport = false,
    status,
    statusHerkomst,
  }: {
    vandaag?: Date;
    binnenkort?: number;
    uitExport?: boolean;
    /** De serverstatus (funding.status). Meegegeven = hij wint; zie de kop. */
    status?: ServerStatus;
    /** funding.statusHerkomst: alleen gebruikt om een 'open' zonder periode te labelen. */
    statusHerkomst?: string;
  } = {},
): VensterStand {
  if (status) {
    const basis = standVanServer(venster, status, vandaag, binnenkort, uitExport, statusHerkomst);
    // Gesloten en verlopen dragen de herkomst van het venster al in de uitleg.
    if (!uitExport || basis.soort === "gesloten" || basis.soort === "verlopen") return basis;
    return {
      ...basis,
      tekst: `${basis.tekst} · redactie-export`,
      kleur: color.accentAmber,
      achtergrond: color.accentAmberBg,
      uitleg:
        "De officiële pagina noemt zelf geen aanvraagperiode; deze komt uit de " +
        "redactie-export van vindsubsidies.nl (aug 2026), waarvan de redactie ook " +
        "niet-publieke reglementen las. Controleer het bij de verstrekker.",
    };
  }
  // De zesde stand (owner-besluit 25-08, optie 1 op de 74 "geen periode
  // vermeld"): de periode komt uit de vindsubsidies-redactie-export, niet van
  // de officiële pagina. De waarde is bruikbaar — hun redactie las reglementen
  // die niet publiek staan — maar hij draagt zijn herkomst zichtbaar en kleurt
  // amber in plaats van groen: een belofte uit tweede hand is geen belofte van
  // de bron. De vlag `aanvraagperiode_uit_export` op de regeling is de schakel.
  if (uitExport) {
    const basis = vensterStand(venster, { vandaag, binnenkort });
    return {
      ...basis,
      tekst: `${basis.tekst} · redactie-export`,
      kleur: basis.soort === "verlopen" ? basis.kleur : color.accentAmber,
      achtergrond: basis.soort === "verlopen" ? basis.achtergrond : color.accentAmberBg,
      uitleg:
        "De officiële pagina noemt zelf geen aanvraagperiode; deze komt uit de " +
        "redactie-export van vindsubsidies.nl (aug 2026), waarvan de redactie ook " +
        "niet-publieke reglementen las. Controleer het bij de verstrekker.",
    };
  }
  const totStart = dagenTot(venster.start, vandaag);
  const totEind = dagenTot(venster.eind, vandaag);

  if (venster.soort === "doorlopend") {
    return {
      soort: "doorlopend",
      tekst: "doorlopend",
      kleur: color.accentGreen,
      achtergrond: "#f0fdf4",
      dagen: null,
      uitleg: "Je kunt het hele jaar door aanvragen; de pagina noemt geen sluitingsdatum.",
    };
  }

  if (venster.soort === "onbekend" || (totStart === null && totEind === null)) {
    return {
      soort: "onbekend",
      tekst: "geen periode vermeld",
      kleur: color.soft,
      achtergrond: color.surfaceHover,
      dagen: null,
      uitleg:
        "De officiële pagina noemt geen aanvraagperiode. Dat is wat wij daar " +
        "aantroffen, geen aanname dat de regeling doorlopend open staat — " +
        "controleer het bij de verstrekker.",
    };
  }

  if (totEind !== null && totEind < 0) {
    return {
      soort: "verlopen",
      tekst: `gesloten sinds ${fmtDatum(venster.eind)}`,
      kleur: color.soft,
      achtergrond: color.surfaceHover,
      dagen: totEind,
      uitleg: "Het aanvraagvenster is voorbij. Een nieuw venster staat nog niet op de pagina.",
    };
  }

  if (totStart !== null && totStart > 0) {
    return {
      soort: "opent",
      tekst: `opent ${fmtDatum(venster.start)}`,
      kleur: color.accentAmber,
      achtergrond: color.accentAmberBg,
      dagen: totStart,
      uitleg:
        totStart <= binnenkort
          ? `Nog niet open: aanvragen kan over ${totStart} dagen. Dit is het moment om een werkgever te bellen.`
          : "Nog niet open; de openingsdatum staat vast.",
    };
  }

  if (totEind !== null && totEind <= binnenkort) {
    return {
      soort: "sluit",
      tekst: totEind === 0 ? "sluit vandaag" : `sluit over ${totEind} dagen`,
      kleur: color.accentRed,
      achtergrond: "#fef2f2",
      dagen: totEind,
      uitleg: `Het venster sluit op ${fmtDatum(venster.eind)}. Daarna kan er niet meer worden aangevraagd.`,
    };
  }

  return {
    soort: "open",
    tekst: venster.eind ? `open t/m ${fmtDatum(venster.eind)}` : "open",
    kleur: color.accentGreen,
    achtergrond: "#f0fdf4",
    dagen: totEind,
    uitleg: venster.eind
      ? `Aanvragen kan tot en met ${fmtDatum(venster.eind)}.`
      : "Het venster is open.",
  };
}

/**
 * De regel onder de badge (W5): de statustekst van de server, letterlijk. Die
 * eindigt altijd met de slotzin over de controle (sof.funding.status.SLOTZINNEN).
 * Draagt een oudere server nog geen statustekst, dan alleen de datum van de
 * laatste geslaagde controle — en bij null zeggen we dat er geen bekend is.
 * Ontbreken beide velden (een server van vóór W0), dan null: niets beweren.
 */
export function statusRegel(f: {
  statusTekst?: string;
  herkomst: { laatstBevestigd?: string | null };
}): string | null {
  if (f.statusTekst) return f.statusTekst;
  const d = f.herkomst.laatstBevestigd;
  if (d === undefined) return null;
  return d
    ? `Laatst bevestigd op de officiële pagina op ${fmtDatum(d)}.`
    : "Geen geslaagde controle op de officiële pagina bekend.";
}
